import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { apiService } from "../../services/axios";
import { toast } from "react-toastify";

export const getMockQuestion = createAsyncThunk(
  "mockTest/startMockTest",
  async (params, { rejectWithValue }) => {
    try {
      const response = await apiService.post(
        "/student/start-mock-exam",
        params,
        { skipErrorToast: true }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const saveAnswer = createAsyncThunk(
  "mockTest/saveAnswer",
  async ({ examId, questionNumber, answer }, { getState, rejectWithValue }) => {
    try {
      const { questionsList } = getState().mockTests;
      const currentQuestion = questionsList[questionNumber];

      if (!currentQuestion) {
        throw new Error("Question not found");
      }

      const payload = {
        examId: examId,
        question_number: questionNumber,
        answer: answer || "",
      };

      const response = await apiService.post(
        "/student/subcategory/submit_answer",
        payload
      );

      return {
        questionNumber,
        answer: answer,
        result: response.data.answeredQuestion?.result,
        meta: response.data.meta,
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const markForReviewQuestion = createAsyncThunk(
  "mockTest/questionMarkForReview",
  async ({ examId, questionNumber, markForReview }, { rejectWithValue }) => {
    try {
      const response = await apiService.post("/student/added-mark-for-review", {
        examId: examId,
        question_number: questionNumber,
        markReview: markForReview,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const endMockExam = createAsyncThunk(
  "mockTest/endMockExam",
  async (_id, { getState, rejectWithValue }) => {
    try {
      const mockTestId = _id || getState().mockTests.mockTestId;
      const response = await apiService.post("/student/end-mock-exam", {
        _id: mockTestId,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const initialState = {
  mockTestId: null,
  questionsList: {},
  currentQuestionNumber: 1,
  loading: false,
  error: null,
  stats: {
    validAnswer: [],
    inValidAnswer: [],
    notAnswered: 0,
    markedForReview: [],
    totalQuestions: 0,
  },
  isCompleted: false,
};

const mockTestSlice = createSlice({
  name: "mockTests",
  initialState,
  reducers: {
    setCurrentQuestionIndex: (state, action) => {
      state.currentQuestionNumber = action.payload;
    },
    setCurrentQuestionAnswer: (state, action) => {
      state.questionsList[action.payload.questionNumber] = {
        ...state.questionsList[action.payload.questionNumber],
        selectedAnswer: action.payload.answer,
      };
    },
    resetMockTest: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(getMockQuestion.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getMockQuestion.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.mockTestId = payload.examId;
        const { question, meta, totalQuestions } = payload;
        state.questionsList[question.questionNumber] = {
          ...state.questionsList[question.questionNumber],
          ...question,
        };
        state.stats.totalQuestions =
          totalQuestions || state.stats.totalQuestions;
        if (meta) {
          state.stats = {
            ...state.stats,
            inValidAnswer: meta.invalidQue || state.stats.inValidAnswer,
            markedForReview:
              meta.markForReviewQue || state.stats.markedForReview,
            notAnswered: meta.notAns || state.stats.notAnswered,
            validAnswer: meta.validQue || state.stats.validAnswer,
          };
        }
        state.currentQuestionNumber = question.questionNumber;
      })
      .addCase(getMockQuestion.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload?.message || "Failed to start mock test";
      })
      .addCase(saveAnswer.pending, (state) => {
        state.loading = true;
      })
      .addCase(saveAnswer.fulfilled, (state, { payload }) => {
        const { questionNumber, answer, result, meta } = payload;

        state.questionsList[questionNumber] = {
          ...state.questionsList[questionNumber],
          userAnswer: answer,
          result,
        };

        if (meta) {
          state.stats = {
            ...state.stats,
            validAnswer: meta.validQue || state.stats.validAnswer,
            inValidAnswer: meta.invalidQue || state.stats.inValidAnswer,
            markedForReview:
              meta.markForReviewQue || state.stats.markedForReview,
            notAnswered: meta.notAns || 0,
          };
        }

        state.loading = false;
      })
      .addCase(saveAnswer.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload?.message || "Failed to save answer";
        toast.error(payload.error);
      })
      .addCase(markForReviewQuestion.pending, (state) => {
        state.loading = true;
      })
      .addCase(markForReviewQuestion.fulfilled, (state, { payload }) => {
        state.loading = false;
        const { updatedQuestion, meta } = payload;
        state.questionsList[updatedQuestion.questionNumber] = {
          ...state.questionsList[updatedQuestion.questionNumber],
          ...updatedQuestion,
        };
        if (meta) {
          state.stats = {
            ...state.stats,
            inValidAnswer: meta.invalidQue || state.stats.inValidAnswer,
            markedForReview:
              meta.markForReviewQue || state.stats.markedForReview,
            notAnswered: meta.notAns || state.stats.notAnswered,
            validAnswer: meta.validQue || state.stats.validAnswer,
          };
        }
      })
      .addCase(markForReviewQuestion.rejected, (state) => {
        state.loading = false;
      })
      .addCase(endMockExam.pending, (state) => {
        state.loading = true;
      })
      .addCase(endMockExam.fulfilled, (state) => {
        state.loading = false;
        state.isCompleted = true;
      })
      .addCase(endMockExam.rejected, (state) => {
        state.loading = false;
      })
      .addMatcher(
        (action) => action.type.endsWith("/rejected"),
        (state, { payload }) => {
          state.loading = false;
          state.error = payload?.message || "Something went wrong";
        }
      );
  },
});

export const {
  setCurrentQuestionIndex,
  setCurrentQuestionAnswer,
  resetMockTest,
} = mockTestSlice.actions;

export default mockTestSlice.reducer;
